import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CompaniesClass, CompaniesAddressClass, CompaniesImagesClass } from '../models/companies-class';
import { HttpclienterrorService } from '../interceptor/httpclienterror.service';
import { CrmCacheService, CrmSwrOptions } from './crm-cache.service';

@Injectable({
  providedIn: 'root',
})
export class CompaniesService {
  constructor(
    private http: HttpClient,
    public httpclienterrorService: HttpclienterrorService,
    private crmCache: CrmCacheService,
  ) {}

  GetCompaniesClaimsidCustomer(idCustomer: number): Observable<CompaniesClass[]> {
    return this.http
      .get<CompaniesClass[]>(environment.URL + `/companies/GetCompaniesClaimsidCustomer/${idCustomer}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementGetCompanie(idCompanie: number): Observable<CompaniesClass[]> {
    return this.http.get<CompaniesClass[]>(environment.URL + `/companies/ManagementGetCompanie/${idCompanie}`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  ManagementSetCompanies(companiesClass: CompaniesClass, uuid: string): Observable<CompaniesClass[]> {
    companiesClass.UUIDLastChange = uuid;
    return this.http.post<CompaniesClass[]>(environment.URL + `/companies/ManagementSetCompanies`, companiesClass).pipe(
      tap(() => this.crmCache.invalidateCurrentScope()),
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCompanie(idCompanie: number): Observable<CompaniesClass[]> {
    return this.http.get<CompaniesClass[]>(environment.URL + `/companies/GetCompanie/${idCompanie}`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  ManagementGetCompaniesALL(options?: CrmSwrOptions): Observable<CompaniesClass[]> {
    return this.crmCache.swr(
      'companies_mgmt_all',
      () =>
        this.http.get<CompaniesClass[]>(environment.URL + `/companies/ManagementGetCompaniesALL`).pipe(
          catchError(error => {
            const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
            return throwError(() => errorMessage);
          }),
        ),
      { freshTtlMs: 5 * 60 * 1000, silentPrefetch: true, ...options },
    );
  }

  GetCompaniesAll(options?: CrmSwrOptions): Observable<CompaniesClass[]> {
    return this.crmCache.swr(
      'companies_all',
      () =>
        this.http.get<CompaniesClass[]>(environment.URL + `/companies/GetCompaniesAll`).pipe(
          catchError(error => {
            const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
            return throwError(() => errorMessage);
          }),
        ),
      { freshTtlMs: 5 * 60 * 1000, silentPrefetch: true, ...options },
    );
  }

  GetCompanieAllStatus(): Observable<CompaniesClass[]> {
    return this.http.get<CompaniesClass[]>(environment.URL + `/companies/GetCompanieAllStatus`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCompaniesClaimsAll(): Observable<CompaniesClass[]> {
    return this.http.get<CompaniesClass[]>(environment.URL + `/companies/GetCompaniesClaimsAll`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  // spGetCompanie
  GetCompanieAddress(idCompanie: number): Observable<CompaniesAddressClass[]> {
    return this.http.get<CompaniesAddressClass[]>(environment.URL + `/companies/GetCompanieAddress/${idCompanie}`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCompanieImages(idCompanie: number): Observable<CompaniesImagesClass[]> {
    return this.http.get<CompaniesImagesClass[]>(environment.URL + `/companies/GetCompanieImages/${idCompanie}`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCompaniesAllActives(options?: CrmSwrOptions): Observable<CompaniesClass[]> {
    return this.crmCache.swr(
      'companies_all_actives',
      () =>
        this.http.get<CompaniesClass[]>(environment.URL + `/companies/GetCompaniesAllActives`).pipe(
          catchError(error => {
            const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
            return throwError(() => errorMessage);
          }),
        ),
      { freshTtlMs: 5 * 60 * 1000, silentPrefetch: true, ...options },
    );
  }

  // ─── NOVO PAINEL DE CONTACTOS DAS SEGURADORAS & REDES (Feedback #391) ───

  getContactsPanel(includeInactive: boolean = false): Observable<any[]> {
    const params = includeInactive ? '?includeInactive=true' : '';
    return this.http.get<any[]>(environment.URL + `/companies/contacts-panel${params}`).pipe(
      catchError(error => {
        return this.http.get<any>('assets/data/contactos-seguradoras.json').pipe(
          catchError(() => {
            const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
            return throwError(() => errorMessage);
          }),
        );
      }),
    );
  }

  saveCompanyContacts(item: any): Observable<any> {
    return this.http.post<any>(environment.URL + `/companies/contacts-panel`, item).pipe(
      tap(() => this.crmCache.invalidateCurrentScope()),
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  deleteCompanyContacts(id: number): Observable<any> {
    return this.http.delete<any>(environment.URL + `/companies/contacts-panel/${id}`).pipe(
      tap(() => this.crmCache.invalidateCurrentScope()),
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  getNetworkCatalog(): Observable<any[]> {
    return this.http.get<any[]>(environment.URL + `/companies/contacts-panel/catalog`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  saveNetworkCatalog(item: any): Observable<any> {
    return this.http.post<any>(environment.URL + `/companies/contacts-panel/catalog`, item).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  deleteNetworkCatalog(id: number): Observable<any> {
    return this.http.delete<any>(environment.URL + `/companies/contacts-panel/catalog/${id}`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }
}
